import moreappIconBrand from "../../assets/moreapp-icon-brand.svg";
import { useTranslation } from "../../state/i18n.tsx";
import { createSignal, Match, Switch } from "solid-js";
import ButtonLink from "../../components/link/ButtonLink.tsx";
import { minutes } from "../../utils/timeUtil.ts";
import Text from "../../components/text/Text.tsx";
import { Translation } from "../../utils/Translation.tsx";
import { keyFromSelector } from "i18next";
import type { SignupRequest } from "../../services/SignupService.ts";
import SignupService from "../../services/SignupService.ts";

type VerifyEmailProps = {
  signupRequest: SignupRequest;
};

const VerifyEmail = (props: VerifyEmailProps) => {
  const { t } = useTranslation();
  const [resent, setResent] = createSignal(false);
  const signupMutation = SignupService.signup();

  const resend = async () => {
    await signupMutation.mutateAsync(props.signupRequest, { onSuccess: () => setResent(true) });

    setTimeout(() => setResent(false), minutes(1));
  };

  return (
    <div class="flex flex-col gap-20">
      <div class="flex flex-col items-center gap-10 desktop:items-start">
        <img src={moreappIconBrand} class="size-12" alt={t(($) => $.MOREAPP_LOGO_IMG_ALT)} />
        <div class="flex w-full flex-col gap-1 text-center desktop:text-left">
          <Switch fallback={<VerificationSent resend={resend} email={props.signupRequest.emailAddress} />}>
            <Match when={resent()}>
              <VerificationResent email={props.signupRequest.emailAddress} />
            </Match>
          </Switch>
        </div>
      </div>
    </div>
  );
};

type VerificationSentProps = {
  resend: () => void;
  email: string;
};

const VerificationSent = (props: VerificationSentProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Text as="h1" class="text-header-3xl text-primary" text={t(($) => $.SIGNUP_PAGE_VERIFICATION_SENT_TITLE)} />
      <div class="flex flex-col gap-6">
        <span class="text-medium-regular break-all text-secondary">
          <Translation
            key={keyFromSelector(($) => $.SIGNUP_PAGE_VERIFICATION_SENT_SENT)}
            options={{ email: props.email }}
            components={{
              b: (props) => <span class="text-medium-semibold text-secondary">{props.children}</span>,
            }}
          />
        </span>
        <Text class="text-medium-regular text-secondary" text={t(($) => $.SIGNUP_PAGE_VERIFICATION_SENT_CHECK_INBOX)} />
        <div class="flex justify-center gap-1 text-center desktop:justify-start desktop:text-left">
          <Text
            class="text-medium-regular text-secondary"
            text={t(($) => $.SIGNUP_PAGE_VERIFICATION_SENT_DID_NOT_RECEIVE_EMAIL)}
          />
          <ButtonLink label={t(($) => $.SIGNUP_PAGE_VERIFICATION_SENT_RESEND)} onClick={props.resend} />
        </div>
      </div>
    </>
  );
};

type VerificationResentProps = {
  email: string;
};

const VerificationResent = (props: VerificationResentProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Text as="h1" class="text-header-3xl text-primary" text={t(($) => $.SIGNUP_PAGE_VERIFICATION_RESENT_TITLE)} />
      <div class="flex flex-col gap-6 text-center desktop:text-left">
        <span class="text-medium-regular text-secondary">
          <Translation
            key={keyFromSelector(($) => $.SIGNUP_PAGE_VERIFICATION_RESENT_RESENT)}
            options={{ email: props.email }}
            components={{
              b: (props) => <span class="text-medium-semibold text-secondary">{props.children}</span>,
            }}
          />
        </span>
        <Text
          class="text-medium-regular text-secondary"
          text={t(($) => $.SIGNUP_PAGE_VERIFICATION_RESENT_CHECK_SPAM)}
        />
      </div>
    </>
  );
};

export default VerifyEmail;
